import type { ScreenContext } from '@/shared/screen';

type Listener = () => void;

// Sem caixa: o CNPJ alfanumérico pode chegar em minúsculas.
const DOCUMENT = /\b(\d{3}\.?\d{3}\.?\d{3}-?\d{2}|[A-Z0-9]{2}\.?[A-Z0-9]{3}\.?[A-Z0-9]{3}\/?[A-Z0-9]{4}-?\d{2})\b/gi;

/** Marcador do valor que não chega ao agente: `toJSON` que lança ou estrutura funda demais. */
export const CONTEXT_OMITTED = '[omitido]';

/** Além disso, o valor vira o marcador: um `toJSON` que gera objetos sem fim não estoura a pilha. */
const MAX_DEPTH = 32;

export function maskDocuments(value: string): string {
  return value.replace(DOCUMENT, (doc) => `${'•'.repeat(Math.max(doc.length - 2, 0))}${doc.slice(-2)}`);
}

/** CPF ou CNPJ guardado como número sai mascarado (como texto); outro número fica como está. */
function maskNumber(value: number | bigint): unknown {
  const text = String(value);
  const masked = maskDocuments(text);
  return masked === text ? value : masked;
}

interface Walk {
  readonly ancestors: WeakSet<object>;
  /** `false`: o valor vai como está (o Luy, U56); só o que não vira JSON é tratado. */
  readonly mask: boolean;
}

/** Chave mascarada sem colisão: dois documentos com o mesmo final não apagam um ao outro. */
function maskedEntries(entries: readonly (readonly [string, unknown])[], walk: Walk, depth: number): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, item] of entries) {
    const masked = walk.mask ? maskDocuments(key) : key;
    let unique = masked;
    for (let n = 2; Object.hasOwn(out, unique); n++) unique = `${masked} (${String(n)})`;
    out[unique] = maskDeep(item, walk, depth);
  }
  return out;
}

/**
 * Mascara documento em todo texto e número do valor, nas chaves e dentro de objetos, listas, `Map` e `Set` (o contexto
 * vai ao agente). `Date` e quem tem `toJSON` viram o que o JSON mostraria (uma chamada por valor, como o
 * `JSON.stringify`); `toJSON` que lança e estrutura funda demais viram `CONTEXT_OMITTED`. Só o ciclo é cortado.
 * Sem máscara (`walk.mask === false`), faz só a parte do JSON.
 */
function maskDeep(value: unknown, walk: Walk, depth = 0, viaJson = false): unknown {
  if (typeof value === 'string') return walk.mask ? maskDocuments(value) : value;
  if (typeof value === 'number') return walk.mask ? maskNumber(value) : value;
  if (typeof value === 'bigint') return walk.mask ? maskNumber(value) : String(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) return CONTEXT_OMITTED;
  const { ancestors } = walk;
  if (ancestors.has(value)) return undefined;
  ancestors.add(value);
  const next = depth + 1;
  try {
    if (Array.isArray(value)) return value.map((item) => maskDeep(item, walk, next));
    if (value instanceof Set) return [...value].map((item) => maskDeep(item, walk, next));
    if (value instanceof Map) {
      const entries = [...(value as Map<unknown, unknown>).entries()];
      return entries.every(([key]) => typeof key === 'string' || typeof key === 'number')
        ? maskedEntries(entries.map(([key, item]) => [String(key), item] as const), walk, next)
        : entries.map(([key, item]) => [maskDeep(key, walk, next), maskDeep(item, walk, next)]);
    }
    const toJson = (value as { toJSON?: unknown }).toJSON;
    if (!viaJson && typeof toJson === 'function') {
      let json: unknown;
      try {
        json = toJson.call(value);
      } catch {
        return CONTEXT_OMITTED;
      }
      return maskDeep(json, walk, next, true);
    }
    return maskedEntries(Object.entries(value), walk, next);
  } finally {
    ancestors.delete(value);
  }
}

/** Qualquer valor que vai a um agente de fora (WebMCP do navegador), com a mesma máscara dos campos do contexto (U24). */
export function maskForAgent(value: unknown): unknown {
  return maskDeep(value, { ancestors: new WeakSet(), mask: true });
}

/** O valor como JSON seguro, sem máscara: o que o Luy recebe (U56, L09). */
function plainForAgent(value: unknown): unknown {
  return maskDeep(value, { ancestors: new WeakSet(), mask: false });
}

/** O contexto para agente de fora (U24): documento mascarado e `restricted` sem campos. */
export function sanitizeContext(ctx: ScreenContext): ScreenContext {
  const summary = maskDocuments(ctx.summary);
  if (ctx.sensitivity === 'restricted' || !ctx.fields) return { ...ctx, summary, fields: {} };
  return { ...ctx, summary, fields: maskForAgent(ctx.fields) as Readonly<Record<string, unknown>> };
}

/** O contexto para o Luy (U56, L09): tudo o que a tela publica, sem máscara, inclusive com `restricted`. */
function plainContext(ctx: ScreenContext): ScreenContext {
  return ctx.fields ? { ...ctx, fields: plainForAgent(ctx.fields) as Readonly<Record<string, unknown>> } : { ...ctx };
}

export function createScreenContextStore() {
  let current: (ScreenContext & { screenId: string }) | null = null;
  let masked: (ScreenContext & { screenId: string }) | null = null;
  let active: string | null = null;
  const listeners = new Set<Listener>();
  return {
    /** O contexto da tela para o Luy, sem máscara (U56). */
    get: () => current,
    /** O mesmo, mascarado, para o `get_screen_context` que vai ao WebMCP do navegador (U24). */
    getMasked: () => masked,
    /** A tela montada e acessível agora, com ou sem contexto (o `get_screen_guide` parte dela). */
    activeScreen: () => active,
    enter(screenId: string) {
      active = screenId;
    },
    leave(screenId: string) {
      if (active === screenId) active = null;
    },
    set(screenId: string, ctx: ScreenContext | null) {
      current = ctx ? { ...plainContext(ctx), screenId } : null;
      masked = ctx ? { ...sanitizeContext(ctx), screenId } : null;
      listeners.forEach((l) => l());
    },
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export type ScreenContextStore = ReturnType<typeof createScreenContextStore>;
